/**
 * @file Liste les clients connus qui ressemblent à la saisie en cours, sélectionnables d'un clic ou au clavier.
 * @module packages.common.components.customer.CustomerMatchList
 */

import PropTypes from 'prop-types';
import {Box, Stack, Avatar, Typography, List, ListItemButton, CircularProgress, Paper} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import PhoneIcon from '@mui/icons-material/PhoneOutlined';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmailOutlined';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';

import {getFormattedPhone} from 'packages/common/utils';

import CustomerFlags from './CustomerFlags';
import {SHARED_CONTACT} from './hooks/useCustomerMatches';
import {getInitials, getClientColor} from './utils/customerUtils';

/** Hauteur maximale de la liste avant défilement (environ cinq lignes). */
const LIST_MAX_HEIGHT = 290;

/** Titre du panneau quand les fiches proposées partagent seulement un contact. */
const SHARED_CONTACT_TITLES = Object.freeze({
  [SHARED_CONTACT.EMAIL]: 'Email déjà utilisé par',
  [SHARED_CONTACT.PHONE]: 'Téléphone déjà utilisé par',
  [SHARED_CONTACT.BOTH]: 'Contact déjà utilisé par',
});

/**
 * Liste de correspondances client.
 *
 * @param {Object} props - Props du composant
 * @param {Array<Object>} props.matches - Fiches trouvées
 * @param {boolean} props.isLoading - Recherche en cours
 * @param {boolean} props.isActive - Une saisie suffisante est en cours
 * @param {string|null} [props.sharedContact] - Contact partagé quand les fiches viennent de la recherche de secours
 * @param {number} props.highlightedIndex - Ligne mise en avant au clavier (-1 si aucune)
 * @param {Function} props.onSelect - Callback (fiche) à la sélection
 * @param {Function} props.onHighlight - Callback (index) au survol
 * @return {React.ReactElement|null} Liste, message d'état, ou rien hors saisie
 */
export default function CustomerMatchList({matches, isLoading, isActive, sharedContact = null, highlightedIndex, onSelect, onHighlight}) {
  const theme = useTheme();
  const isSharedContact = Boolean(sharedContact);
  const title = isSharedContact ? SHARED_CONTACT_TITLES[sharedContact] : 'Clients correspondants';

  if (!isActive) return null;

  if (matches.length === 0) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{mt: 1.5, color: 'text.secondary', minHeight: 24}}>
        {isLoading ? (
          <>
            <CircularProgress size={14} color="inherit" />
            <Typography variant="caption">Recherche parmi vos clients...</Typography>
          </>
        ) : (
          <>
            <PersonAddAltIcon sx={{fontSize: 16}} />
            <Typography variant="caption">Aucun client connu ne correspond. La fiche sera créée avec la réservation.</Typography>
          </>
        )}
      </Stack>
    );
  }

  // Panneau sobre et distinct du formulaire : en-tête navy du dialogue maison,
  // bordure fine, lignes aérées. La proposition doit se remarquer avant que
  // l'utilisateur ne finisse de remplir une fiche en double, sans crier.
  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{px: 2, py: 1.25, bgcolor: 'navy.900', color: 'common.white'}}
      >
        <PersonSearchIcon sx={{fontSize: 18, opacity: 0.9}} />
        <Typography
          variant="overline"
          sx={{flexGrow: 1, lineHeight: 1.4, letterSpacing: '0.08em', fontWeight: 600, opacity: 0.9}}
        >
          {title}
        </Typography>
        {isLoading ? (
          <CircularProgress size={14} color="inherit" />
        ) : (
          <Box
            component="span"
            sx={{
              minWidth: 24,
              px: 1,
              py: 0.25,
              borderRadius: 10,
              bgcolor: 'common.white',
              color: 'navy.900',
              fontSize: 12,
              fontWeight: 700,
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            {matches.length}
          </Box>
        )}
      </Stack>
      <Box sx={{maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto', bgcolor: 'background.paper'}}>
        <List dense disablePadding role="listbox" aria-label={title}>
          {matches.map((customer, index) => (
            <ListItemButton
              key={customer._id}
              role="option"
              aria-selected={index === highlightedIndex}
              selected={index === highlightedIndex}
              onClick={() => onSelect(customer)}
              onMouseEnter={() => onHighlight(index)}
              divider={index < matches.length - 1}
              sx={{
                'px': 2,
                'py': 1.25,
                'position': 'relative',
                '&.Mui-selected, &.Mui-selected:hover': {bgcolor: 'grey.100'},
                '&.Mui-selected::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 8,
                  bottom: 8,
                  width: 3,
                  borderRadius: 3,
                  bgcolor: 'navy.900',
                },
              }}
            >
              <CustomerMatchRow customer={customer} theme={theme} />
            </ListItemButton>
          ))}
        </List>
      </Box>
      {isSharedContact && (
        // Le serveur ne réutilise une fiche trouvée par contact que si le nom
        // coïncide : sans clic, la réservation crée bien une nouvelle fiche.
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{px: 2, py: 1.25, borderTop: `1px solid ${theme.palette.divider}`, color: 'text.secondary'}}
        >
          <PersonAddAltIcon sx={{fontSize: 16}} />
          <Typography variant="caption">
            Autre personne ? Une nouvelle fiche sera créée avec la réservation.
          </Typography>
        </Stack>
      )}
    </Paper>
  );
}

/**
 * Ligne d'une correspondance : avatar, nom, drapeaux et moyens de contact.
 *
 * @param {Object} props - Props du composant
 * @param {Object} props.customer - Fiche client
 * @param {Object} props.theme - Thème MUI
 * @return {React.ReactElement} Contenu de la ligne
 */
function CustomerMatchRow({customer, theme}) {
  const phone = getFormattedPhone(customer);
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{width: '100%', minWidth: 0}}>
      <Avatar sx={{bgcolor: getClientColor(customer.lastName, theme), color: '#fff', width: 36, height: 36, fontSize: '0.875rem'}}>
        {getInitials(customer.firstName, customer.lastName)}
      </Avatar>
      <Box sx={{flexGrow: 1, minWidth: 0}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
          <Typography variant="subtitle2" noWrap>
            {customer.firstName} {customer.lastName}
          </Typography>
          <CustomerFlags customer={customer} size="small" />
        </Box>
        <Stack direction="row" spacing={2} sx={{minWidth: 0}}>
          {customer.email && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{display: 'flex', alignItems: 'center'}}>
              <AlternateEmailIcon fontSize="inherit" sx={{mr: 0.5}} />
              {customer.email}
            </Typography>
          )}
          {phone && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{display: 'flex', alignItems: 'center', flexShrink: 0}}>
              <PhoneIcon fontSize="inherit" sx={{mr: 0.5}} />
              {phone}
            </Typography>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}

CustomerMatchRow.propTypes = {
  customer: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
};

CustomerMatchList.propTypes = {
  matches: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool.isRequired,
  isActive: PropTypes.bool.isRequired,
  sharedContact: PropTypes.oneOf(Object.values(SHARED_CONTACT)),
  highlightedIndex: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired,
  onHighlight: PropTypes.func.isRequired,
};
